import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ApiService } from "../../api";
import type {
  AdminUser,
  Location,
  LocationInUse,
  LocationNode,
  OrgUnit,
} from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { SectionLabel } from "../../components/surfaces";
import { space } from "../../theme/space";

const { Text } = Typography;

type Props = {
  units: OrgUnit[];
  onChanged: () => void;
};

const KINDS = ["Site", "Area", "Building", "Zone", "Point", "Other"];

/**
 * Where work happens: sites, the areas inside them, and the places people type.
 *
 * **Two tables, and the second one is the point.** A master-data list is easy
 * to build and worthless if nobody's data matches it. Every deployment already
 * has months of free text in `Request.location` and `CustomUser.site` — that
 * text *is* the client's vocabulary — so the panel shows it beside the list and
 * says which of it lands nowhere. Adding a place from an unmatched value is one
 * click, and the count beside it says how much of the data is riding on it.
 *
 * A **tree**, like the org chart, because the questions climb it: "everything
 * on this site" has to include the areas inside it.
 *
 * Nothing here converts the free-text columns. Promoting one of them to a
 * foreign key while the other stayed text would put two vocabularies for one
 * idea into the system, and it would rewrite history on cases already raised.
 */
const LocationsPanel = ({ units, onChanged }: Props) => {
  const { t } = useTranslation();
  const [tree, setTree] = useState<LocationNode[]>([]);
  const [flat, setFlat] = useState<Location[]>([]);
  const [inUse, setInUse] = useState<LocationInUse[]>([]);
  const [unmatched, setUnmatched] = useState(0);
  const [people, setPeople] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nodes, rows, used, everyone] = await Promise.all([
        ApiService.apiAccountsLocationsTree(),
        ApiService.apiAccountsLocationsList(),
        ApiService.apiAccountsLocationsInUse(),
        ApiService.apiAccountsAdminUsersList({ is_active: "true" }),
      ]);
      setTree(nodes);
      setFlat(rows);
      setInUse(used.values);
      setUnmatched(used.unmatched);
      setPeople(everyone);
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the places")));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (place: Location | null, parentId?: number, name?: string) => {
    setCreating(place === null);
    setEditing(place);
    form.setFieldsValue(
      place ?? {
        name: name ?? "",
        // Derived from the name the same way the import command derives it, so
        // adding a place by hand and importing one in bulk agree.
        code: name ? suggestCode(name) : "",
        kind: "Site",
        parent: parentId ?? null,
        manager: null,
        org_unit: null,
        address: "",
      },
    );
  };

  const save = async () => {
    const values = await form.validateFields();
    try {
      if (creating) await ApiService.apiAccountsLocationsCreate(values);
      else
        await ApiService.apiAccountsLocationsPartialUpdate(editing!.id, values);
      setEditing(null);
      setCreating(false);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not save the place")));
    }
  };

  const retire = (node: LocationNode) => {
    Modal.confirm({
      title: t("Retire {{name}}?", { name: node.name }),
      content:
        node.usage_count > 0
          ? t(
              "It stops being offered, but nothing is deleted — {{count}} record(s) still name this place and keep their history.",
              { count: node.usage_count },
            )
          : t(
              "It stops being offered anywhere. Nothing is deleted — anything that named it keeps its history.",
            ),
      okText: t("Retire it"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await ApiService.apiAccountsLocationsDelete(node.id);
          load();
          onChanged();
        } catch (e) {
          message.error(apiErrorMessage(e, t("Could not retire the place")));
        }
      },
    });
  };

  const columns = [
    {
      title: t("Place"),
      dataIndex: "name",
      render: (name: string, row: LocationNode) => (
        <Space size={6}>
          <span style={{ fontWeight: 500 }}>{name}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.code}
          </Text>
          <Tag style={{ marginInlineEnd: 0 }}>{t(row.kind)}</Tag>
        </Space>
      ),
    },
    {
      title: t("Responsible"),
      dataIndex: "manager_name",
      render: (value: string) => value || <Text type="secondary">—</Text>,
    },
    {
      title: t("In use"),
      dataIndex: "usage_count",
      width: 110,
      render: (count: number) =>
        count > 0 ? (
          <Tooltip
            title={t(
              "How many cases and people already name this place in free text.",
            )}
          >
            <span>{count}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 220,
      render: (_: unknown, row: LocationNode) => (
        <Space size={4}>
          <Button
            size="small"
            onClick={() => open(flat.find((l) => l.id === row.id) ?? null)}
          >
            {t("Edit")}
          </Button>
          <Button size="small" onClick={() => open(null, row.id)}>
            {t("Add inside")}
          </Button>
          <Button size="small" danger onClick={() => retire(row)}>
            {t("Retire")}
          </Button>
        </Space>
      ),
    },
  ];

  const inUseColumns = [
    {
      title: t("Typed value"),
      dataIndex: "value",
      render: (value: string, row: LocationInUse) => (
        <Space size={6}>
          <span>{value}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.source === "people"
              ? t("on people")
              : row.source === "cases"
                ? t("on cases")
                : t("on cases and people")}
          </Text>
        </Space>
      ),
    },
    { title: t("Times used"), dataIndex: "count", width: 120 },
    {
      title: t("Matches"),
      key: "matched",
      render: (_: unknown, row: LocationInUse) =>
        row.matched ? (
          row.matched_name
        ) : (
          <Text type="secondary">{t("not in the list")}</Text>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_: unknown, row: LocationInUse) =>
        row.matched ? null : (
          <Button size="small" onClick={() => open(null, undefined, row.value)}>
            {t("Add as a place")}
          </Button>
        ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: space.snug,
          marginBottom: space.snug,
        }}
      >
        <SectionLabel>{t("Places")}</SectionLabel>
        {/* Beside its label, not flex-pushed to the far edge of a 1,700px
            row — a primary action a screen away from the thing it acts on is
            a primary action nobody finds. */}
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => open(null)}
        >
          {t("New place")}
        </Button>
      </div>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={tree}
        columns={columns as any}
        pagination={false}
        // Everything open: the nesting is what somebody came here to read.
        defaultExpandAllRows
        locale={{
          emptyText: t(
            "No places yet. Add the sites and areas work happens on, and everything that records a place can start agreeing on their names.",
          ),
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: space.snug,
          margin: `${space.band}px 0 ${space.snug}px`,
        }}
      >
        <SectionLabel style={{ flex: 1 }}>
          {t("Places typed by hand")}
          {unmatched > 0 && (
            <span style={{ fontWeight: 400, textTransform: "none" }}>
              {" · "}
              {unmatched === 1
                ? t("1 not in the list")
                : t("{{count}} not in the list", { count: unmatched })}
            </span>
          )}
        </SectionLabel>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(
          "Cases and people still record a place as free text. Anything not in the list counts as its own place in every filter and report.",
        )}
      </Text>
      <Table
        size="small"
        rowKey="value"
        style={{ marginTop: space.snug }}
        dataSource={inUse}
        columns={inUseColumns as any}
        pagination={false}
        locale={{
          emptyText: t("Nothing has recorded a place yet."),
        }}
      />

      <Modal
        open={editing !== null || creating}
        title={creating ? t("New place") : t("Edit place")}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={save}
        okText={t("Save")}
        cancelText={t("Cancel")}
      >
        <Form form={form} layout="vertical" style={{ marginTop: space.base }}>
          <Form.Item name="name" label={t("Name")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="code"
            label={t("Code")}
            rules={[{ required: true }]}
            extra={t(
              "A short, stable handle like KS-3. Free text is matched against this as well as the name, ignoring case, spaces and dashes — so KS 3 and ks3 both find it.",
            )}
          >
            <Input />
          </Form.Item>
          <div style={{ display: "flex", gap: space.snug }}>
            <Form.Item name="kind" label={t("Kind")} style={{ flex: 1 }}>
              <Select
                options={KINDS.map((k) => ({ value: k, label: t(k) }))}
              />
            </Form.Item>
            <Form.Item name="parent" label={t("Inside")} style={{ flex: 1 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={flat
                  .filter((l) => l.id !== editing?.id)
                  .map((l) => ({ value: l.id, label: l.full_name || l.name }))}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="manager"
            label={t("Responsible person")}
            extra={t("Who answers for this place. Optional.")}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={people.map((p) => ({
                value: p.id,
                label: `${p.name} · ${p.email}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="org_unit" label={t("Run by")}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={units.map((u) => ({ value: u.id, label: u.name }))}
            />
          </Form.Item>
          <Form.Item name="address" label={t("Address")}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

/** 'Compressor station 3' -> 'COMPRESSOR-STATION-3'.
 *
 *  The same rule `import_locations.derive_code` uses, so a place added by hand
 *  and the same place imported in bulk end up with one code rather than two. */
const suggestCode = (name: string) =>
  (name || "")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .join("-")
    .toUpperCase()
    .slice(0, 64);

export default LocationsPanel;
